import React from "react";
import PropTypes from "prop-types";
import {
    Tabs,
    Pane,
    RadioCheckGroup,
    DatePicker
} from "@pearson-components/elements-sdk/build/dist.elements-sdk";
import { Link } from "react-router-dom";
import PriceUpdate from "./PriceUpdate";
import { Scrollbars } from "react-custom-scrollbars";
import uniqBy from "lodash/uniqBy";
import sortBy from "lodash/sortBy";
import isEqual from "lodash/isEqual";
import moment from "moment";
import ActionCard from "../../widgets/ActionCard/ActionCard";
import IconWithLabel from "../../widgets/IconWithLabel/IconWithLabel";
import ModalDialog from "../../widgets/ModalDialog/ModalDialog";
import config from "../../../config";
import axiosClient from "../../../_helpers/http.helper";
import UpdateVisibility from "./UpdateVisibility";
import {
    METADATA_VALIDATION_ERRORS,
    TEXT_TYPES
} from "../../../constants/APP_CONSTANTS";

export default class MetadataEditPanel extends React.Component {
    static propTypes = {
        metadata: PropTypes.array.isRequired,
        resources: PropTypes.array.isRequired,
        selected: PropTypes.array.isRequired,
        clearSelected: PropTypes.func.isRequired,
        refreshSearch: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        this.state = {
            validationRules: {
                businessCases: [],
                texts: []
            },
            metadataFields: [],
            metadataUpdates: {},
            metadataUpdatesValidity: {},
            batchId: "",
            isConfirmationModalActive: false,
            isBatchCompleteModalActive: false
        };
        this.renderFormElementByDataType = this.renderFormElementByDataType.bind(
            this
        );
        this.renderMetadataEditForm = this.renderMetadataEditForm.bind(this);
        this.renderMetadataFieldsList = this.renderMetadataFieldsList.bind(
            this
        );
        this.onMetadataFormEdit = this.onMetadataFormEdit.bind(this);
        this.onMetadataFieldSelect = this.onMetadataFieldSelect.bind(this);
        this.validateFields = this.validateFields.bind(this);
    }

    componentDidMount() {
        const metadataValidationsRoute
            = config.contentService.baseUrl
            + config.contentService.routes.getMetadataValidations;

        axiosClient.get(metadataValidationsRoute).then(response => {
            this.setState({ validationRules: response.data });
        });
    }

    static getDerivedStateFromProps(props, state) {
        const metadataFields = [];

        const hasCases = props.resources.find(
            resource => resource.category === "Case"
        );
        const hasTexts = props.resources.find(
            resource => resource.category === "Textbook"
        );
        const hasReadings = props.resources.find(
            resource => resource.category === "Reading"
        );

        if (hasCases || hasReadings) {
            metadataFields.push(...state.validationRules["businessCases"]);
        }

        if (hasTexts) {
            metadataFields.push(...state.validationRules["texts"]);
        }

        const unupdatableMetadataFields = ["aHeadDescription", "epsItemId", "unscrubbed"];

        const result = sortBy(
            uniqBy(metadataFields, "pojoAttributeName").filter(
                field => !!field.pojoAttributeName && !unupdatableMetadataFields.includes(field.pojoAttributeName)
            ),
            ["pojoAttributeName"]
        );
        return {
            metadataFields: result
        };
    }

    componentDidUpdate(prevProps, prevState) {
        if (!isEqual(this.state.metadataUpdates, prevState.metadataUpdates)) {
            this.validateFields();
        }
    }

    getSelectedResults = () => {
        return this.props.metadata.filter(metadataItem =>
            this.props.selected.includes(metadataItem.csgResourceId)
        );
    };

    getDefaultValueByField = fieldKey => {
        const field = this.state.metadataFields[fieldKey];
        if (field.dataType === "BOOLEAN") {
            return false;
        }

        return "";
    };

    onMetadataFieldSelect(key) {
        this.setState(prevState => {
            if (prevState.metadataUpdates.hasOwnProperty(key)) {
                const newMetadataUpdates = {};
                Object.entries(prevState.metadataUpdates).forEach(
                    ([fieldKey, value]) => {
                        if (fieldKey !== key) {
                            newMetadataUpdates[fieldKey] = value;
                        }
                    }
                );
                return {
                    metadataUpdates: newMetadataUpdates
                };
            } else {
                return {
                    metadataUpdates: {
                        ...prevState.metadataUpdates,
                        [key]: this.getDefaultValueByField(key)
                    }
                };
            }
        });
    }

    validateFields() {
        const metadataUpdatesValidity = {};

        Object.entries(this.state.metadataUpdates).map(([fieldKey, value]) => {
            const options = this.state.metadataFields[fieldKey];
            let validationError = null;

            switch (options.dataType) {
                case "STRING": {
                    const stringValue = value ? value.trim() : value;
                    if (options.mandatory && !stringValue) {
                        validationError
                            = METADATA_VALIDATION_ERRORS.IS_REQUIRED;
                    } else if (
                        options.size
                        && stringValue
                        && options.size < stringValue.length
                    ) {
                        validationError
                            = METADATA_VALIDATION_ERRORS.DATA_TOO_LONG;
                    }
                    break;
                }
                case "INTEGER": {
                    const isValidNumber = Number.isInteger(
                        Number.parseInt(value)
                    );

                    if (!isValidNumber) {
                        validationError
                            = METADATA_VALIDATION_ERRORS.INVALID_NUMBER;

                        if (value === "") {
                            validationError = options.mandatory
                                ? METADATA_VALIDATION_ERRORS.IS_REQUIRED
                                : null;
                        }
                    } else if (
                        options.size
                        && options.size < value.toString(10).length
                    ) {
                        validationError
                            = METADATA_VALIDATION_ERRORS.NUMBER_TOO_LARGE;
                    }
                    break;
                }
                case "DECIMAL": {
                    const isValidNumber = Number.isInteger(
                        Number.parseInt(value)
                    );

                    if (!isValidNumber) {
                        validationError
                            = METADATA_VALIDATION_ERRORS.INVALID_NUMBER;

                        if (value === "") {
                            validationError = options.mandatory
                                ? METADATA_VALIDATION_ERRORS.IS_REQUIRED
                                : null;
                        }
                    }

                    break;
                }
                case "DATE": {
                    if (options.mandatory && !value) {
                        validationError
                            = METADATA_VALIDATION_ERRORS.IS_REQUIRED;
                    }

                    if (value) {
                        if (!moment(value).isValid()) {
                            validationError
                                = METADATA_VALIDATION_ERRORS.INVALID_DATE;
                        }
                    }
                }
            }

            metadataUpdatesValidity[fieldKey] = validationError;
        });

        this.setState({ metadataUpdatesValidity });
    }

    onMetadataFormEdit(fieldKey, value) {
        this.setState(prevState => ({
            metadataUpdates: {
                ...prevState.metadataUpdates,
                [fieldKey]: value
            }
        }));
    }

    updateBatchDetailModalState = (batchId, status) => {
        this.setState({
            batchId: batchId,
            isBatchCompleteModalActive: status
        });
    };

    updateMetadata = () => {
        this.setState({
            isConfirmationModalActive: false
        });

        const metadataUpdateRoute
            = config.contentService.baseUrl
            + config.contentService.routes.metadata;
        const updateValues = {};

        Object.entries(this.state.metadataUpdates).forEach(
            ([fieldKey, value]) => {
                const fieldType = this.state.metadataFields[fieldKey].dataType;
                const savedValue = (value && fieldType === "STRING") ? value.trim() : value;

                updateValues[
                    this.state.metadataFields[fieldKey].pojoAttributeName
                ] = savedValue;
            }
        );

        const requestBody = this.props.selected.map(csgResourceId => ({
            csgResourceId,
            ...this.props.metadata.find(metadataItem => metadataItem.csgResourceId === csgResourceId),
            ...updateValues
        }));

        axiosClient.put(metadataUpdateRoute, requestBody).then(response => {
            this.setState({
                batchId: response.data,
                isBatchCompleteModalActive: true
            });
        });
    };

    isResourcePublic = csgResourceId => {
        const resource = this.props.resources.find(
            resource => resource.resourceId === csgResourceId
        );
        return resource && resource.sharing === "Public" ? true : false;
    };

    renderPreviewTableContent = () => {
        return this.getSelectedResults().map((item, key) => (
            <div className="table-items" key={key}>
                <div className="thumbnail">
                    {item.thumbnailUrl ? (
                        <img src={item.thumbnailUrl} />
                    ) : (
                        <div className="placeholder">No Image</div>
                    )}
                </div>
                <div className="table-content">
                    <div className="table-content-left">
                        <h3 className="title-text">{item.assetTitle}</h3>
                        <p>{item.publicDescription}</p>
                    </div>
                    <div className="table-content-right">
                        <span className="badge badge-info">
                            {this.isResourcePublic(item.csgResourceId)
                                ? "Visible"
                                : "Hidden"}
                        </span>
                    </div>
                </div>
            </div>
        ));
    };

    renderMetadataFieldsList() {
        return (
            <RadioCheckGroup
                id="metadata-fields"
                inputType="checkbox"
                options={this.state.metadataFields.map((field, key) => ({
                    value: key,
                    label: field.pojoAttributeName,
                    checked: this.state.metadataUpdates.hasOwnProperty(key)
                }))}
                changeHandler={e => this.onMetadataFieldSelect(e.target.value)}
            />
        );
    }

    renderFormElementByDataType(fieldKey, value, options, validationError) {
        switch (options.dataType) {
            case "STRING":
                if (options.size && options.size < 256) {
                    return (
                        <input
                            key={fieldKey}
                            type="text"
                            id={`field-${fieldKey}`}
                            className={
                                !validationError
                                    ? "pe-textInput--basic"
                                    : "pe-textInput--basic_error"
                            }
                            value={value || ""}
                            onChange={e =>
                                this.onMetadataFormEdit(
                                    fieldKey,
                                    e.target.value
                                )
                            }
                        />
                    );
                } else {
                    return (
                        <textarea
                            key={fieldKey}
                            className={
                                !validationError
                                    ? "pe-multiLineText"
                                    : "pe-multiLineText--error"
                            }
                            id={`field-${fieldKey}`}
                            rows="2"
                            value={value || ""}
                            onChange={e =>
                                this.onMetadataFormEdit(
                                    fieldKey,
                                    e.target.value
                                )
                            }
                        />
                    );
                }
            case "BOOLEAN":
                return (
                    <RadioCheckGroup
                        key={fieldKey}
                        id={`field-${fieldKey}`}
                        inputType="checkbox"
                        options={[
                            {
                                value: fieldKey,
                                label: options.pojoAttributeName,
                                checked: value
                            }
                        ]}
                        changeHandler={() =>
                            this.onMetadataFormEdit(fieldKey, !value)
                        }
                    />
                );
            case "INTEGER":
                return (
                    <input
                        key={fieldKey}
                        type="text"
                        id={`field-${fieldKey}`}
                        className={
                            !validationError
                                ? "pe-textInput--basic"
                                : "pe-textInput--basic_error"
                        }
                        value={value || ""}
                        onChange={e =>
                            this.onMetadataFormEdit(fieldKey, e.target.value)
                        }
                    />
                );
            case "DECIMAL":
                return (
                    <input
                        key={fieldKey}
                        type="text"
                        id={`field-${fieldKey}`}
                        className={
                            !validationError
                                ? "pe-textInput--basic"
                                : "pe-textInput--basic_error"
                        }
                        value={value || ""}
                        onChange={e =>
                            this.onMetadataFormEdit(fieldKey, e.target.value)
                        }
                    />
                );
            case "DATE":
                return (
                    <DatePicker
                        key={fieldKey}
                        id={`field-${fieldKey}`}
                        inputState="default"
                        datePickerValue={value || ""}
                        dateFormat="dd/mm/yyyy"
                        changeHandler={newValue => {
                            const formattedDate = moment(
                                newValue,
                                "DD-MM-YYYY"
                            );
                            this.onMetadataFormEdit(
                                fieldKey,
                                formattedDate.isValid()
                                    ? formattedDate.format()
                                    : newValue
                            );
                        }}
                    />
                );
        }
    }

    renderMetadataEditForm() {
        return Object.entries(this.state.metadataUpdates).map(
            ([fieldKey, value]) => {
                const options = this.state.metadataFields[fieldKey];
                const validationError = this.state.metadataUpdatesValidity[
                    fieldKey
                ];

                return (
                    <div className="metadata-form-field" key={fieldKey}>
                        <label
                            className={
                                !validationError
                                    ? "pe-textLabelInput__label"
                                    : "pe-textLabelInput__label--label_error"
                            }
                            htmlFor={`field-${fieldKey}`}
                        >
                            {
                                this.state.metadataFields[fieldKey]
                                    .pojoAttributeName
                            }
                        </label>
                        {this.renderFormElementByDataType(
                            fieldKey,
                            value || "",
                            options,
                            validationError
                        )}
                        {validationError && (
                            <p className="pe-input--error_message">
                                {validationError}
                            </p>
                        )}
                    </div>
                );
            }
        );
    }

    render() {
        return (
            <div className="metadata-edit-panel">
                <div className="resource-preview-table">
                    <ActionCard
                        title="Files Selected"
                        controlsRight={
                            <IconWithLabel
                                icon="remove-sm-24"
                                label="Deselect All"
                                onClick={this.props.clearSelected}
                            />
                        }
                    >
                        <Scrollbars autoHeight autoHeightMax={300}>
                            {this.renderPreviewTableContent()}
                        </Scrollbars>
                    </ActionCard>
                </div>

                <div className="content-edit-pane">
                    <label className="pe-title">
                        {TEXT_TYPES.EDIT_CONTENT_MANAGEMENT}
                    </label>
                    <hr />

                    <Tabs selected={0}>
                        <Pane label="Visibility">
                            <UpdateVisibility
                                selectedResources={this.props.selected}
                                setModalState={this.updateBatchDetailModalState}
                            />
                        </Pane>
                        <Pane label="Metadata">
                            <div className="pe-row">
                                <div className="pe-col-6">
                                    <ActionCard
                                        title="Select metadata for updation here"
                                        controlsRight={
                                            <IconWithLabel
                                                icon="check-sm-24"
                                                label="Select All"
                                                onClick={() => {
                                                    const metadataUpdates = {};
                                                    this.state.metadataFields.forEach(
                                                        (field, fieldkey) => {
                                                            metadataUpdates[
                                                                fieldkey
                                                            ] = "";
                                                        }
                                                    );
                                                    this.setState({
                                                        metadataUpdates
                                                    });
                                                }}
                                            />
                                        }
                                    >
                                        <div className="metadata-fields-list">
                                            <Scrollbars
                                                autoHeight
                                                autoHeightMax={500}
                                            >
                                                {this.renderMetadataFieldsList()}
                                            </Scrollbars>
                                        </div>
                                    </ActionCard>
                                </div>
                                <div className="pe-col-6">
                                    {Object.keys(this.state.metadataUpdates)
                                        .length > 0 && (
                                        <ActionCard
                                            controlsRight={
                                                <IconWithLabel
                                                    icon="remove-sm-24"
                                                    label="Deselect All"
                                                    onClick={() =>
                                                        this.setState({
                                                            metadataUpdates: {}
                                                        })
                                                    }
                                                />
                                            }
                                        >
                                            <Scrollbars
                                                autoHeight
                                                autoHeightMax={500}
                                            >
                                                <div className="metadata-form">
                                                    {this.renderMetadataEditForm()}
                                                    <button
                                                        type="button"
                                                        className="pe-btn pe-btn__primary"
                                                        onClick={() =>
                                                            this.setState({
                                                                isConfirmationModalActive: true
                                                            })
                                                        }
                                                        disabled={Object.values(
                                                            this.state
                                                                .metadataUpdatesValidity
                                                        ).find(i => !!i)}
                                                    >
                                                        {
                                                            TEXT_TYPES.UPDATE_METADATA
                                                        }
                                                    </button>
                                                </div>
                                            </Scrollbars>
                                        </ActionCard>
                                    )}
                                </div>
                            </div>
                        </Pane>
                        <Pane label="Pricing">
                            <PriceUpdate
                                selectedResources={this.props.selected}
                                refreshSearch={this.props.refreshSearch}
                            />
                        </Pane>
                    </Tabs>
                </div>

                <ModalDialog
                    title="Metadata Attributes for Updating"
                    showModal={this.state.isConfirmationModalActive}
                    onCancel={() =>
                        this.setState({ isConfirmationModalActive: false })
                    }
                    onSuccess={this.updateMetadata}
                    successText="OK"
                >
                    <div className="metadata-edit-dialog">
                        <p>{TEXT_TYPES.UPDATE_METADATA_CONFIRMATION}</p>
                        <div>
                            {Object.entries(this.state.metadataUpdates).map(
                                ([fieldKey, value], key) => (
                                    <div key={key} className="metadata-list">
                                        <span className="metadata-name">
                                            {
                                                this.state.metadataFields[
                                                    fieldKey
                                                ].pojoAttributeName
                                            }
                                        </span>
                                        <span className="metadata-value">
                                            {value === ""
                                                ? "NULL"
                                                : value.toString().trim()}
                                        </span>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </ModalDialog>

                <ModalDialog
                    title="Batch ID Information"
                    showModal={this.state.isBatchCompleteModalActive}
                    onSuccess={() => {
                        this.setState({ isBatchCompleteModalActive: false });
                        this.props.refreshSearch();
                    }}
                    showCancel={false}
                    showCloseControls={false}
                    successText="OK"
                >
                    <div className="modal-body">
                        <div className="m-2">
                            {TEXT_TYPES.UPLOAD_BATCH_INFO_MSG}{" "}
                            {this.state.batchId}.{" "}
                            <Link to="/metadata-update-status">
                                <b>{TEXT_TYPES.VIEW_STATUS_MSG}</b>
                            </Link>
                        </div>
                    </div>
                </ModalDialog>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/pages/ContentManagement/MetadataEditPanel.js